// Bundle parents reject parent_id. Nest keys match hybrid-cart.
function nestBundleItems(items) {
  if (!items.some((item) => item.parent_id != null)) return items
  const nestId = crypto.randomUUID()
  return items.map((item) => {
    if (item.parent_id == null) {
      return { ...item, properties: { ...(item.properties || {}), _HC_NEST_ID: nestId } }
    }
    const { parent_id, ...rest } = item
    return { ...rest, properties: { ...(rest.properties || {}), _HC_PARENT_KEY: nestId } }
  })
}

class DeviceProductForm extends HTMLElement {
  constructor() {
    super()

    // START A/B Test code
    if (window.NOOD_TEST && document.querySelector('[data-is-nood-test]')) {
      // Show Nood Test
      document
        .querySelector('.items-baseline [data-main-product-compare-at-price]')
        .parentElement.classList.add('flex', 'flex-row-reverse', 'items-center', 'gap-1')
      document.querySelector('[data-is-nood-test]').dataset.isNoodTest = true
      document.querySelectorAll('[data-nood-test-active]').forEach((el) => el.classList.remove('hidden'))

      // Remove Control
      document.querySelectorAll('[data-nood-test-control]').forEach((el) => el.remove())
    }
    // END A/B Test code

    this.isNoodTest = document.querySelector('[data-is-nood-test]')?.dataset.isNoodTest ?? false
    if (this.isNoodTest == 'true') {
      this.setupNoodAddToCart()
    } else {
      this.setupAddToCart()
    }
    this.setupPriceUpdating()
    this.setupVariantImageUpdating()
  }

  // Set by the custom.out_of_stock metafield: blocks add to cart regardless of Shopify inventory,
  // e.g. bundles whose components are discontinued.
  get isForcedOutOfStock() {
    return this.hasAttribute('data-force-out-of-stock')
  }

  attachBackorderProperty(items) {
    // ponytail: PDP/LP device form + PLP card ATC only. Stamp from metafields in hybrid-cart if cart upsells start selling backordered devices.
    const message = this.getAttribute('data-backorder-message')
    if (!message || !items?.length) return
    items[0].properties = { ...(items[0].properties || {}), Backorder: message }
  }

  setupNoodAddToCart() {
    const serumBundleData = document.querySelector('[data-serum-bundle-data]')

    const serumBundleProducts = serumBundleData ? JSON.parse(serumBundleData.innerHTML) : null

    const $$addToCartButtons = document.querySelectorAll('[data-add-to-cart]')

    $$addToCartButtons.forEach(($addToCartButton) =>
      $addToCartButton.addEventListener('click', () => {
        if (this.isForcedOutOfStock) return
        $addToCartButton.dataset.loading = null

        // Desired display: main → serum bundle lines → warranty → selected add-ons.
        // executeAddToCart reverses for Shopify cart/add.js prepend behavior.
        const parentVariantId = Number(this.getAttribute('variant-id'))
        const items = []

        const bundleDiscountScriptProperty = this.querySelector('[data-bundle-discount-script-property]')
          ? this.querySelector('[data-bundle-discount-script-property]').dataset.bundleDiscountScriptProperty
          : null

        const $purchaseOption = this.querySelector('[name="purchase-option"]:checked').value
        let selling_plan_id = ''
        let variant_id = ''

        items.push({
          quantity: 1,
          id: parentVariantId,
          properties: {
            [$purchaseOption && $purchaseOption == 'serum-bundle'
              ? bundleDiscountScriptProperty
              : '_device_only_exclude']: true,
          },
        })

        if ($purchaseOption && $purchaseOption == 'serum-bundle') {
          serumBundleProducts.forEach((product) => {
            if (product.selling_plan_groups) {
              product.selling_plan_groups.forEach((group) => {
                group.selling_plans.forEach((plan) => {
                  if (plan.name.includes('4 weeks')) {
                    selling_plan_id = plan.id
                  }

                  product.variants.forEach((variant) => {
                    if (variant.title.includes('1-Month') || variant.title.includes('1-Pack')) {
                      variant_id = variant.id
                    }
                  })
                })
              })
            }

            items.push({
              quantity: 1,
              id: variant_id,
              selling_plan: selling_plan_id,
              properties: { [bundleDiscountScriptProperty]: true },
              parent_id: parentVariantId,
            })
          })
        }

        const $warranty = this.querySelector('[name="warranty"]:checked')
        if ($warranty && $warranty.value != '') {
          items.push({ quantity: 1, id: Number($warranty.value), parent_id: parentVariantId })
        }

        const $$addons = this.querySelectorAll('[name="addon-product"]:checked')
        $$addons.forEach(($addon) => {
          if ($addon.value == '') return
          items.push({
            quantity: 1,
            id: Number($addon.value),
            properties: { _addon: true },
            parent_id: parentVariantId,
          })
        })

        const addToCartPayload = { items }

        this.executeAddToCart(addToCartPayload, $addToCartButton)
      })
    )
  }

  setupAddToCart() {
    const $$addToCartButtons = document.querySelectorAll('[data-add-to-cart]')

    $$addToCartButtons.forEach(($addToCartButton) =>
      $addToCartButton.addEventListener('click', () => {
        if (this.isForcedOutOfStock) return
        $addToCartButton.dataset.loading = null

        const addToCartPayload = this.buildAddToCartPayload()

        this.executeAddToCart(addToCartPayload, $addToCartButton)
      })
    )

    const $deviceOnlyRadio = this.querySelector('#device-only')
    if ($deviceOnlyRadio) {
      $deviceOnlyRadio.addEventListener('change', (e) => {
        if (e.target && e.target.checked) {
          document.dispatchEvent(new CustomEvent('device-form:device-only-selected'))
        }
      })
    }

    const $upgradeRadio = this.querySelector('#product-upgrade-radio')
    if ($upgradeRadio) {
      $upgradeRadio.addEventListener('change', (e) => {
        if (e.target && e.target.checked) {
          document.dispatchEvent(new CustomEvent('device-form:upgrade-selected'))
        }
      })
    }
  }

  buildAddToCartPayload() {
    // Desired cart display order (top → bottom):
    // main → warranty → auto-addons/GWP → selected add-ons.
    // cart/add.js prepends each line (reversing the batch), so executeAddToCart
    // reverses this array before posting.
    const items = []
    let parentVariantId = Number(this.getAttribute('variant-id'))

    const $kit = this.querySelector('[name="kit"]:checked')
    if ($kit && $kit.value != '') {
      parentVariantId = Number($kit.value)
    }

    items.push({ quantity: 1, id: parentVariantId })

    const $productUpgrade = this.querySelector('[name="product-upgrade"]:checked')
    if ($productUpgrade && $productUpgrade.value != '') {
      items.push({
        quantity: 1,
        id: Number($productUpgrade.value),
        properties: { _upgrade: true },
        parent_id: parentVariantId,
      })
    }

    const $warranty = this.querySelector('[name="warranty"]:checked')
    if ($warranty && $warranty.value != '') {
      items.push({ quantity: 1, id: Number($warranty.value), parent_id: parentVariantId })
    }

    try {
      const autoAddons = JSON.parse(this.getAttribute('auto-addons') || '[]')
      if (Array.isArray(autoAddons)) {
        autoAddons.forEach((addon) => items.push({ ...addon, parent_id: parentVariantId }))
      }
    } catch (error) {
      console.error('Error parsing autoAddons:', error)
    }

    const $$addons = document.querySelectorAll('[name="addon-product"]:checked')
    $$addons.forEach(($addon) => {
      if ($addon.value == '') return
      items.push({
        quantity: 1,
        id: Number($addon.value),
        properties: { _addon: true },
        parent_id: parentVariantId,
      })
    })

    return { items }
  }

  executeAddToCart(addToCartPayload, $addToCartButton) {
    this.attachBackorderProperty(addToCartPayload.items)
    const warrantyIds = [40117523873827, 40117526167587, 40117524463651, 40559546564643, 40559548858403, 40559546630179]
    const hasWarranty = addToCartPayload.items.some((item) => warrantyIds.includes(item.id))

    // Shopify prepends each line in the batch, reversing display order.
    let cartItems = window.igApplyCartNesting([...addToCartPayload.items].reverse())
    if (this.dataset.bundle === 'true') cartItems = nestBundleItems(cartItems)
    const payload = { items: cartItems }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json()
          return alert(error.description || error.message)
        }

        if (hasWarranty) window.igEvents.push({ event: 'warranty-added-to-cart' })
        window.HybridCart.open()
      })
      .catch((response) => {
        console.error('Error adding to cart:', response)
      })
      .finally(() => {
        delete $addToCartButton.dataset.loading
      })
  }

  setupPriceUpdating() {
    const $productVariantsData = this.querySelector('#product-variants-data')
    const productVariants = JSON.parse($productVariantsData.innerHTML)
    const automaticDiscount = Number(this.getAttribute('automatic-discount'))

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      trailingZeroDisplay: 'stripIfInteger',
    })

    const updatePrice = () => {
      const $kit = this.querySelector('[name="kit"]:checked')
      const $warranty = this.querySelector('[name="warranty"]:checked')
      const $$addons = this.querySelectorAll('[name="addon-product"]:checked')
      const $purchaseOption = this.querySelector('[name="purchase-option"]:checked')?.parentElement
      const tagsEl = document.getElementById('product-tags')
      const isTotalValueProduct = tagsEl?.textContent?.includes('Total Value Product') || false
      const $$promoTexts = document.querySelectorAll('[data-promo-text]')
      let price = 0
      let compareAtPrice = 0
      let totalPrice = 0

      if ($purchaseOption && window.NOOD_TEST) {
        const $purchaseOptionPrice = $purchaseOption.querySelector('[data-purchase-option-price]')
        const $purchaseOptionComparePrice = $purchaseOption.querySelector('[data-purchase-option-compare-price]')

        price = parseFloat($purchaseOptionPrice.textContent.replace(/[^0-9.]/g, '')) * 100
        compareAtPrice = $purchaseOptionComparePrice
          ? parseFloat($purchaseOptionComparePrice.textContent.replace(/[^0-9.]/g, '')) * 100
          : 0
      } else {
        let variant = productVariants[0]
        if ($kit && $kit.value != '') {
          const foundVariant = productVariants.find((v) => v.id == $kit.value)
          if (!foundVariant) return console.error('Variant not found')
          variant = foundVariant
        } else {
          const deviceVariantId = Number(this.getAttribute('variant-id'))
          if (!Number.isNaN(deviceVariantId) && deviceVariantId > 0) {
            const match = productVariants.find((v) => Number(v.id) === deviceVariantId)
            if (match) variant = match
          }
        }
        price += variant.price - automaticDiscount
        compareAtPrice += variant.compare_at_price || variant.price
      }

      totalPrice += price

      if ($warranty && $warranty.value != '') {
        totalPrice += parseInt($warranty.dataset.price) || 0
      }

      $$addons.forEach(($addon) => {
        if ($addon.value == '') return
        totalPrice += parseInt($addon.dataset.price) || 0
      })

      const $$price = document.querySelectorAll('[data-main-product-price]')
      const $$compareAtPrice = document.querySelectorAll('[data-main-product-compare-at-price]')
      const $$totalPrice = document.querySelectorAll('[data-main-product-total-price]')

      const formattedPrice = formatter.format(price / 100)
      $$price.forEach(($price) => {
        $price.innerHTML = formattedPrice
      })

      const formattedTotalPrice = formatter.format(totalPrice / 100)
      $$totalPrice.forEach(($totalPrice) => {
        $totalPrice.innerHTML = formattedTotalPrice
      })

      if (compareAtPrice > price) {
        const formattedCompareAtPrice = formatter.format(compareAtPrice / 100)
        $$compareAtPrice.forEach(($compareAtPrice) => {
          $compareAtPrice.innerHTML = isTotalValueProduct
            ? `(Total Value ${formattedCompareAtPrice})`
            : formattedCompareAtPrice
          $compareAtPrice.classList.remove('hidden')
        })
        if ($$promoTexts.length > 0) {
          const formattedPriceDifference = formatter.format((compareAtPrice - price) / 100)
          $$promoTexts.forEach(($promoText) => {
            let currentText = $promoText.textContent
            if (currentText.length === 0) {
              const $promoOriginalText = document.querySelector('[data-promo-original-text]')
              if ($promoOriginalText) {
                currentText = $promoOriginalText.dataset.promoOriginalText
              }
            }
            // Replace just the $ ammount
            $promoText.textContent = currentText.replace(/\$[\d,]+(?:\.\d{2})?/, formattedPriceDifference)
          })
        }
      } else {
        $$compareAtPrice.forEach(($compareAtPrice) => $compareAtPrice.classList.add('hidden'))
      }

      const affirmEl = this.querySelector('[data-body-affirm-ala]')
      if (affirmEl) {
        affirmEl.setAttribute('data-amount', String(Math.round(totalPrice)))
        if (typeof window.affirm === 'object' && affirm.ui && typeof affirm.ui.refresh === 'function') {
          try {
            affirm.ui.refresh()
          } catch (_) {}
        }
      }

      this.emitSelectionUpdate(formatter, productVariants, automaticDiscount)
    }

    const $$selectors = this.querySelectorAll(
      '[name="kit"], [name="product-upgrade"], [name="warranty"], [name="addon-product"], [name="purchase-option"]'
    )
    $$selectors.forEach(($selector) => $selector.addEventListener('change', updatePrice))
    // init
    updatePrice()
  }

  formatMoney(cents, formatter) {
    if (!Number.isFinite(cents)) return ''
    return formatter.format(cents / 100)
  }

  getVariantImageUrl(variant) {
    const featured = variant?.featured_image
    if (!featured) return this.getAttribute('data-product-image') || ''
    if (typeof featured === 'string') return featured
    return featured.src || featured.url || this.getAttribute('data-product-image') || ''
  }

  resolveSelectedVariant(productVariants) {
    const $kit = this.querySelector('[name="kit"]:checked')
    if ($kit && $kit.value !== '') {
      const kitVariant = productVariants.find((v) => String(v.id) === String($kit.value))
      if (kitVariant) return kitVariant
    }

    const deviceVariantId = Number(this.getAttribute('variant-id'))
    if (!Number.isNaN(deviceVariantId) && deviceVariantId > 0) {
      const match = productVariants.find((v) => Number(v.id) === deviceVariantId)
      if (match) return match
    }

    return productVariants[0] || null
  }

  buildMainSelectionItem(variant, automaticDiscount, formatter) {
    const sellCents = Math.max(0, Number(variant?.price || 0) - Number(automaticDiscount || 0))
    const compareAtCents = Number(variant?.compare_at_price || variant?.price || sellCents)
    const title = this.getAttribute('data-product-title') || variant?.name || 'Product'
    const image = this.getVariantImageUrl(variant)
    const imageAlt = this.getAttribute('data-product-image-alt') || title

    return {
      type: 'product',
      title,
      variantId: Number(variant?.id || this.getAttribute('variant-id')),
      quantity: 1,
      image,
      imageAlt,
      url: this.getAttribute('data-product-url') || window.location.pathname,
      unitPriceCents: sellCents,
      unitCompareAtPriceCents: compareAtCents,
      linePriceCents: sellCents,
      lineCompareAtPriceCents: compareAtCents,
      price: this.formatMoney(sellCents, formatter),
      compareAtPrice: this.formatMoney(compareAtCents, formatter),
    }
  }

  buildUpgradeSelectionItem(formatter) {
    const $upgrade = this.querySelector('[name="product-upgrade"]:checked')
    if (!($upgrade instanceof HTMLInputElement) || !$upgrade.value) return null

    // Prefer the card override (e.g. "+$50") for CYR display; fall back to variant price.
    const sellCents = Number($upgrade.dataset.price || $upgrade.dataset.originalPrice || 0)
    const compareAtCents = Number($upgrade.dataset.originalPrice || sellCents)
    const title =
      $upgrade.dataset.productTitle ||
      $upgrade.closest('.fmp-upgrade-card')?.querySelector('.fmp-upgrade-card__title')?.textContent?.trim() ||
      'Upgrade'
    const image =
      $upgrade.dataset.imageUrl ||
      $upgrade.closest('.fmp-upgrade-card')?.querySelector('.fmp-upgrade-card__image img')?.getAttribute('src') ||
      ''

    return {
      type: 'upgrade',
      title,
      variantId: Number($upgrade.value),
      quantity: 1,
      image,
      imageAlt: $upgrade.dataset.imageAlt || title,
      url: $upgrade.dataset.productUrl || '',
      unitPriceCents: sellCents,
      unitCompareAtPriceCents: compareAtCents,
      linePriceCents: sellCents,
      lineCompareAtPriceCents: compareAtCents,
      price: this.formatMoney(sellCents, formatter),
      compareAtPrice: this.formatMoney(compareAtCents, formatter),
      properties: { _upgrade: true },
    }
  }

  buildAddonSelectionItems(formatter) {
    return Array.from(this.querySelectorAll('[name="addon-product"]:checked'))
      .filter(($addon) => $addon instanceof HTMLInputElement && $addon.value !== '')
      .map(($addon) => {
        const quantity = Math.max(1, Number($addon.dataset.quantity) || 1)
        const unitSell = Number($addon.dataset.price || 0)
        // data-price on addons is already quantity-adjusted in markup for v2 addons
        const lineSell = $addon.dataset.giftId ? 0 : unitSell
        const lineCompareAt = Number($addon.dataset.compareAtPrice || lineSell)
        const title = $addon.dataset.productTitle || $addon.getAttribute('aria-label') || 'Add-on'

        return {
          type: $addon.dataset.giftId ? 'gift' : 'addon',
          title,
          variantId: Number($addon.value),
          quantity,
          image: $addon.dataset.imageUrl || '',
          imageAlt: $addon.dataset.imageAlt || title,
          url: $addon.dataset.productUrl || '',
          unitPriceCents: lineSell,
          unitCompareAtPriceCents: lineCompareAt,
          linePriceCents: lineSell,
          lineCompareAtPriceCents: lineCompareAt,
          price: lineSell === 0 ? 'Free' : this.formatMoney(lineSell, formatter),
          compareAtPrice: this.formatMoney(lineCompareAt, formatter),
          properties: { _addon: true },
        }
      })
  }

  emitSelectionUpdate(formatter, productVariants, automaticDiscount) {
    const variant = this.resolveSelectedVariant(productVariants)
    if (!variant) return

    const items = [this.buildMainSelectionItem(variant, automaticDiscount, formatter)]
    const upgradeItem = this.buildUpgradeSelectionItem(formatter)
    if (upgradeItem) items.push(upgradeItem)
    items.push(...this.buildAddonSelectionItems(formatter))

    const detail = {
      selection: {
        variantId: Number(variant.id),
        hasUpgrade: Boolean(upgradeItem),
      },
      items,
    }

    window.apexSelectionUpdateDetail = detail
    window.dispatchEvent(new CustomEvent('apex-selection-update', { detail }))
  }

  setupVariantImageUpdating() {
    const hideImage = ($image) => {
      $image.classList.add('opacity-0')
      setTimeout(() => $image.classList.add('hidden'), 300)
    }

    const showImage = ($image) => {
      $image.classList.remove('hidden')
      setTimeout(() => $image.classList.remove('opacity-0'), 50)
    }

    const $section = this.closest('section')
    const $$kitSelectors = $section.querySelectorAll('[name="kit"]')
    $$kitSelectors.forEach(($kitSelector) => {
      $kitSelector.addEventListener('change', () => {
        // Desktop
        const $$desktopVariantImages = $section.querySelectorAll('[data-product-media-desktop] [data-variant-image]')
        if ($$desktopVariantImages.length === 0) return

        const position = $kitSelector.dataset.variantImage
        const $kitDesktopImage = $section.querySelector(
          '[data-product-media-desktop] [data-variant-image="' + position + '"]'
        )

        const $mainImage = $$desktopVariantImages[0].parentNode.querySelector('img:not([data-variant-image])')
        if (position === '') showImage($mainImage)
        else hideImage($mainImage)

        $$desktopVariantImages.forEach(($desktopVariantImage) => {
          if ($desktopVariantImage === $kitDesktopImage) return
          hideImage($desktopVariantImage)
        })

        if ($kitDesktopImage) showImage($kitDesktopImage)

        // Mobile
        const $mainMobileSplide = $section.querySelector('#product-media-splide')
        if (!$mainMobileSplide) return
        $mainMobileSplide.splide.go(parseInt(position) - 1 || 0)
      })
    })
  }
}

customElements.define('device-product-form', DeviceProductForm)
